/**
 * SCWT-PM-V2 Phase 6 · Frontend helpers shared by the PM tab, the
 * PWS Partner corrective-leads page and the Landlord Portal
 * withdraw action. Kept as plain functions + constants so both
 * consumers stay in sync on labels, colours and action-code map.
 */
export const CL_STATUS_LABEL = {
  offered:              "Offered",
  accepted:             "Accepted",
  declined:             "Declined",
  offer_expired:        "Offer expired",
  consent_withdrawn:    "Consent withdrawn",
  contacted:            "Contacted",
  inspection_booked:    "Inspection booked",
  quote_provided:       "Quote provided",
  work_approved:        "Work approved",
  work_completed:       "Work completed",
  not_proceeding:       "Not proceeding",
  unable_to_contact:    "Unable to contact",
  withdrawn_by_pm:      "Withdrawn by agency",
  withdrawn_by_landlord: "Withdrawn by landlord",
  paused:               "Paused",
  reassignment_required: "Re-assignment required",
};

// Tailwind palette per status. Kept centralised so the PM tab, the
// partner board and the landlord panel show the same colour.
export const CL_STATUS_TONE = {
  offered:              "bg-cyan-50 text-cyan-800 border-cyan-200",
  accepted:             "bg-emerald-50 text-emerald-800 border-emerald-200",
  declined:             "bg-slate-100 text-slate-700 border-slate-200",
  offer_expired:        "bg-amber-50 text-amber-800 border-amber-200",
  consent_withdrawn:    "bg-rose-50 text-rose-800 border-rose-200",
  contacted:            "bg-blue-50 text-blue-800 border-blue-200",
  inspection_booked:    "bg-indigo-50 text-indigo-800 border-indigo-200",
  quote_provided:       "bg-violet-50 text-violet-800 border-violet-200",
  work_approved:        "bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200",
  work_completed:       "bg-emerald-100 text-emerald-900 border-emerald-300",
  not_proceeding:       "bg-slate-100 text-slate-700 border-slate-200",
  unable_to_contact:    "bg-amber-50 text-amber-800 border-amber-200",
  withdrawn_by_pm:      "bg-slate-100 text-slate-700 border-slate-200",
  withdrawn_by_landlord: "bg-rose-50 text-rose-800 border-rose-200",
  paused:               "bg-yellow-50 text-yellow-800 border-yellow-200",
  reassignment_required: "bg-orange-50 text-orange-800 border-orange-200",
};

// Mirrors PARTNER_TRANSITIONS in the backend service. Kept here as the
// single source of truth for the partner status-update dropdown.
export const PARTNER_TRANSITIONS = {
  accepted:          ["contacted", "unable_to_contact"],
  contacted:         ["inspection_booked", "quote_provided", "not_proceeding", "unable_to_contact"],
  inspection_booked: ["quote_provided", "not_proceeding"],
  quote_provided:    ["work_approved", "not_proceeding"],
  work_approved:     ["work_completed", "not_proceeding"],
};

// Hours a lead offer stays valid — displayed in the "expires in" hint
// and used by the countdown pill. Must match OFFER_TTL_HOURS on the
// backend service.
export const OFFER_TTL_HOURS = 72;

/** Returns a rough "expires in Xh" label for offer countdown. */
export function offerCountdown(iso) {
  if (!iso) return null;
  try {
    const diffMs = new Date(iso).getTime() - Date.now();
    if (diffMs <= 0) return "expired";
    const hrs = Math.floor(diffMs / 3600000);
    if (hrs >= 24) {
      const days = Math.floor(hrs / 24);
      return `expires in ${days}d ${hrs - days * 24}h`;
    }
    if (hrs >= 1) return `expires in ${hrs}h`;
    const mins = Math.max(1, Math.floor(diffMs / 60000));
    return `expires in ${mins}m`;
  } catch { return null; }
}

/** Formats an ISO date as "Feb 9, 2026 · 2:14 PM AEST"-ish local. */
export function fmtLocal(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-AU",
      { day: "numeric", month: "short", year: "numeric",
        hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}
